import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthCtx = createContext(null);

async function registerPushTokenIfBridge() {
  try {
    const bridge = typeof window !== "undefined" ? window.JTBridge : null;
    if (!bridge || typeof bridge.getPushToken !== "function") return;
    const token = bridge.getPushToken();
    if (!token) return;
    const platform = typeof bridge.getPlatform === "function" ? bridge.getPlatform() : "android";
    await api.post("/users/me/push-token", { token, platform });
  } catch (e) {
    // Silent: web browsers just won't have the bridge, and network failures
    // don't block the user experience.
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("jt_user") || "null"); } catch { return null; }
  });
  const [ready, setReady] = useState(false);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("jt_token");
    if (!token) { setReady(true); return; }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      localStorage.setItem("jt_user", JSON.stringify(data));
      registerPushTokenIfBridge();
    } catch {
      localStorage.removeItem("jt_token");
      localStorage.removeItem("jt_user");
      setUser(null);
    } finally {
      setReady(true);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("jt_token", data.token);
    localStorage.setItem("jt_user", JSON.stringify(data.user));
    setUser(data.user);
    registerPushTokenIfBridge();
    return data.user;
  };

  const logout = async () => {
    try {
      const bridge = typeof window !== "undefined" ? window.JTBridge : null;
      const token = bridge && typeof bridge.getPushToken === "function" ? bridge.getPushToken() : "";
      if (token) {
        await api.delete("/users/me/push-token", { data: { token } });
      }
    } catch { /* ignore */ }
    localStorage.removeItem("jt_token");
    localStorage.removeItem("jt_user");
    setUser(null);
    window.location.href = "/login";
  };

  return (
    <AuthCtx.Provider value={{ user, setUser, ready, login, logout, refresh }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
